// ===== Packages =====

import {
    getFunctions,
    httpsCallable,
    HttpsCallableResult,
}                           from 'firebase/functions';

// ===== Services =====

import normalizeURL from './normalizeURL';
import isTestMode  from './isTestMode';

// ===== Interfaces =====

import {
    ILinkMetadata,
} from '../interfaces';

// ===== Constants =====

import {
    VERASCOPE_WEBSITE_URL,
    VERASCOPE_DOMAIN_NAME,
} from '../constants/generalConstants';

const EMPTY_LINK_METADATA: ILinkMetadata = {
    title: null,
    description: null,
    image: null,
    url: null,
};
const LEARNING_THE_GAME_OF_LIFE_DOMAIN_NAME = 'learningthegameoflife.com';
const BOOK_ROUTE_PATHNAME = '/book';
const INTERNAL_BOOK_HOSTNAMES = [
    VERASCOPE_DOMAIN_NAME,
    `www.${VERASCOPE_DOMAIN_NAME}`,
    LEARNING_THE_GAME_OF_LIFE_DOMAIN_NAME,
    `www.${LEARNING_THE_GAME_OF_LIFE_DOMAIN_NAME}`,
];

const hasResolvedLinkMetadata = (
    metadata: ILinkMetadata | null | undefined,
): boolean => [
    metadata?.title,
    metadata?.description,
    metadata?.image,
    metadata?.url,
].some((value) => `${value || ''}`.trim().length > 0);

/**
 * Avoids an extra callable round-trip for obviously external links while still
 * supporting relative `/book` links inside the editor.
 */
const shouldAttemptInternalLinkMetadata = (url: string): boolean => {
    let parsedURL: URL;
    try {
        parsedURL = new URL(url, VERASCOPE_WEBSITE_URL);
    } catch (_error) {
        return false;
    }

    const normalizedPathname = parsedURL.pathname.replace(/\/+$/g, '') || '/';
    if (normalizedPathname !== BOOK_ROUTE_PATHNAME) {
        return false;
    }

    return INTERNAL_BOOK_HOSTNAMES.includes(parsedURL.hostname.toLowerCase());
};

const fetchURLMetadata = (
    url: string,
    onComplete: (result: HttpsCallableResult<unknown>) => void,
): boolean => {
    const resolveMetadata = async (): Promise<void> => {
        const functions = getFunctions();
        if (shouldAttemptInternalLinkMetadata(url)) {
            try {
                const fetchInternalLinkMetadata = httpsCallable(functions, 'getInternalLinkMetadata');
                const internalResult = await fetchInternalLinkMetadata({
                    url,
                    test: isTestMode(),
                });
                const internalLinkMetadata = internalResult.data as ILinkMetadata;
                if (hasResolvedLinkMetadata(internalLinkMetadata)) {
                    onComplete(internalResult);
                    return;
                }
            } catch (_error) {
                // Fall back to generic metadata so internal callable outages do not
                // blank all link previews.
            }
        }

        const fetchMetadata = httpsCallable(functions, 'getURLMetadata');
        const result = await fetchMetadata({
            url: normalizeURL(url),
        });
        onComplete(result);
    };

    resolveMetadata().catch(() => {
        onComplete({
            data: EMPTY_LINK_METADATA,
        } as HttpsCallableResult<unknown>);
    });

    return true;
};

export default fetchURLMetadata;
